import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  Input,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CanColor, mixinColor } from '@angular/material/core';
import { Constructor } from '@mtna/core-ts';
import { SummaryStatisticSet } from '@mtna/model-predefined-data-ts';

export class RdsVariableDistributionBase {
  constructor(public _elementRef: ElementRef) {}
}

export const _RdsVariableDistributionMixinBase: Constructor<CanColor> & typeof RdsVariableDistributionBase = mixinColor(
  RdsVariableDistributionBase,
  'primary'
);

/**
 * Variable Distribution Component visualizes the Variable's Summary Statistics's min, max, mean and standard deviation.
 *
 * @author Zach Edwards <zach.edwards@mtna.us>
 */

@Component({
  selector: 'rds-c-variable-distribution',
  templateUrl: './variable-distribution.html',
  styleUrls: ['./variable-distribution.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RdsVariableDistributionComponent extends _RdsVariableDistributionMixinBase implements CanColor, DoCheck {
  /** Theme color, defaults to primary */
  @Input() color: 'primary' | 'accent' = 'primary';

  /** Required summary statistic set to display */
  @Input()
  get statSet(): SummaryStatisticSet {
    return this._statSet;
  }
  set statSet(newValue: SummaryStatisticSet) {
    this._statSet = newValue;
    this.calcMeanLeft();
    if (this.sdWidth) {
      this.calcSdWidth();
      this.cdr.markForCheck();
    }
  }
  private _statSet!: SummaryStatisticSet;

  /** References to html element that represents the distribution line */
  @ViewChild('line') lineElem?: ElementRef;

  /** Percentage from beginning of line */
  meanLeft?: string;
  /** Positions mean */
  meanPositionPercent = 0;
  /** Line width */
  rangePxWidth = 0;
  /** Width of standard deviation */
  sdWidth?: string;
  /** Clips left or right side of standard deviation */
  sdTrunc?: string;

  constructor(private cdr: ChangeDetectorRef, elementRef: ElementRef) {
    super(elementRef);
  }

  ngDoCheck() {
    // If the line width has changed since the last DoCheck,
    // recalculate the SD width
    const lineWidth = this.lineElem ? Math.round(this.lineElem.nativeElement.getBoundingClientRect().width) : 0;
    if (!!lineWidth && lineWidth !== this.rangePxWidth) {
      this.calcSdWidth();
    }
  }

  calcMeanLeft() {
    if (this.statSet !== undefined) {
      const range = (this.statSet.max || 0) - (this.statSet.min || 0);
      this.meanPositionPercent = ((this.statSet.mean || 0) - (this.statSet.min || 0)) / range;
      this.meanLeft = this.meanPositionPercent * 100 + '%';
    }
  }

  calcSdWidth() {
    const range = (this.statSet.max || 0) - (this.statSet.min || 0);
    // Grab line width and get distance from standard deviation
    this.rangePxWidth = this.lineElem ? Math.round(this.lineElem.nativeElement.getBoundingClientRect().width) : 0;
    const width = ((this.statSet.standardDeviation || 0) * this.rangePxWidth) / range;
    this.sdWidth = width + 'px';
    // Range from start of line to mean
    const sdStart = this.rangePxWidth * (this.meanPositionPercent || 0) - width / 2;
    // Clip when standard deviation range exceeds line boundaries
    const clipRight = this.rangePxWidth - sdStart + 'px';
    const clipLeft = sdStart < 0 ? Math.abs(sdStart) + 'px' : '-2px';
    this.sdTrunc = `rect(auto, ${clipRight}, auto, ${clipLeft})`;
    this.cdr.markForCheck();
  }
}
